import React, { useState, useEffect } from 'react';
import { usePartner } from '@contexts/PartnerContext';
import { addCarehomePhoneNumber } from '@api/carehome-info';
import SuccessNotificationBanner from '@components/SuccessNotificationBanner';
import { FormError, SubmitStatus, SubmitStatusEnum } from '@typings/common';
import {
  updateErrors,
  getErrorMessage as getErrorMessageFromErrors,
} from '@utils/commons';
import NewErrorSummary from '@components/NewErrorSummary';
import getErrorMessage from '@utils/errorUtils';

interface PhoneNumberProps {
  phoneNumber: string;
  setPhoneNumber: (phone: string) => void;
}

const PhoneNumber: React.FC<PhoneNumberProps> = ({
  phoneNumber,
  setPhoneNumber,
}) => {
  const [errors, setErrors] = useState<FormError[]>([]);
  const [errMessage, setErrMessage] = useState<string | undefined>('');
  const [addPhoneStatus, setAddPhoneStatus] = useState<SubmitStatus>({
    loading: false,
    submitted: SubmitStatusEnum.IDLE,
    message: '',
  });
  const { selectedPartnerId } = usePartner();

  const onSavePhoneNumber = async (e: any) => {
    e.preventDefault();

    let validationErrors: FormError[] = [];
    validationErrors = updateErrors(
      validationErrors,
      'phoneNumber',
      !phoneNumber ? 'Please enter a phone number' : ''
    );

    setErrors(prev => validationErrors);

    if (validationErrors.length !== 0) {
      return;
    }

    if (selectedPartnerId) {
      setAddPhoneStatus(prev => ({ ...prev, loading: true }));
      try {
        const res = await addCarehomePhoneNumber(
          selectedPartnerId,
          phoneNumber
        );

        if (res && res.status === 'success') {
          setAddPhoneStatus(prev => ({
            ...prev,
            submitted: SubmitStatusEnum.SUCCESS,
            message: 'The phone number has been saved',
          }));

          setTimeout(() => {
            setAddPhoneStatus(prev => ({
              ...prev,
              submitted: SubmitStatusEnum.IDLE,
              message: '',
            }));
          }, 3000);
        }
      } catch (error) {
        const errorMessage = getErrorMessage(error);
        console.error(errorMessage);
        setAddPhoneStatus({
          loading: false,
          submitted: SubmitStatusEnum.FAILED,
          message: errorMessage,
        });

        validationErrors = updateErrors(
          validationErrors,
          'addPhoneAPI',
          errorMessage
        );
        setErrors(prev => validationErrors);
      } finally {
        setAddPhoneStatus(prev => ({ ...prev, loading: false }));
      }
    }
  };

  useEffect(() => {
    setErrMessage(getErrorMessageFromErrors(errors, 'phoneNumber'));
  }, [errors]);

  return (
    <div>
      <h2 className="govuk-heading-m">
        Your organisation&apos;s contact phone number
      </h2>
      <p className="govuk-body">
        What is the best phone number to call for patients, their next of kin
        and those granted access to their Advance Care Plan?
      </p>

      <form>
        {addPhoneStatus.submitted === SubmitStatusEnum.SUCCESS && (
          <SuccessNotificationBanner
            title={addPhoneStatus.message}
            duration={3000}
            noScrollToTop
          />
        )}

        {errors.length > 0 && <NewErrorSummary errors={errors} />}
        <div
          className={`govuk-form-group ${errMessage ? 'govuk-form-group--error' : ''}`}
        >
          <label className="govuk-label" htmlFor="phoneNumber">
            Enter phone number
          </label>
          {errMessage && (
            <p id="full-name-input-error" className="govuk-error-message">
              <span className="govuk-visually-hidden">Error:</span>
              {errMessage}
            </p>
          )}

          <input
            className={`govuk-input govuk-input--width-20 ${errMessage ? 'govuk-input--error' : ''}`}
            id="phoneNumber"
            name="phoneNumber"
            type="tel"
            autoComplete="tel"
            aria-describedby="having-conversations-phone-number"
            maxLength={11}
            value={phoneNumber}
            onChange={e => {
              const { value } = e.target;
              if (/^[0-9]*$/.test(value) && value.length <= 11) {
                setPhoneNumber(value);
              }
            }}
          />
        </div>
        <button
          type="submit"
          className="govuk-button"
          disabled={addPhoneStatus.loading}
          onClick={onSavePhoneNumber}
        >
          Save
        </button>
      </form>
    </div>
  );
};

export default PhoneNumber;
